import { Injectable } from '@angular/core';
import { mergeMap, Observable, of } from 'rxjs';

import {
  DprActivitiesGridSync,
  DprActivitiesGridSyncResult,
  DprHseGridSync,
  DprPersonnelGridSync,
  DprWeatherGridSync,
  DprConsumableGridSync,
  DprSummaryGridSyncResult,
  DprSummaryGridSync,
  DprProgressGridSync,
  DprAppendixGridSync,
  DprAppendixGridSyncResult,
  DprHiddenGridSync,
  DprStatusTrackerGridSync,
  DprProgressWithoutTargetGridSync,
  DprFreeTextFieldSyncResult,
  DprForecastActivitiesGridSync,
  DprForecastActivitiesGridSyncResult,
  DprFreeTextFieldSync,
  DprHiddenGridSyncResult,
  DprTableOfColumnGridSync,
} from '@dpr-details/models/sync';
import {
  DprDetails,
  DprWeather,
  DprHse,
  DprPersonnel,
  DprConsumable,
  DprProgress,
  DprStatusTracker,
  DprTableOfColumns,
  DprProgressWithoutTarget,
} from '@shared/models/dpr';
import {
  CopyPreviousDprForecastActivitiesOptions,
  CopyPreviousDprForecastActivitiesResult,
} from '@dpr-details/models';
import { HttpDprDetailsContext, UtcDateTimeOptions } from '@shared/enums';
import { convertDateToString, toUTCDate } from '@shared/utils';
import { DprDetailsSyncSanitizeService } from '../sync';
import { DprDetailsBasePersistenceService } from '.';
import { GenericHttpClientService } from '@shared/services';

@Injectable()
export class DprDetailsRemotePersistenceService extends DprDetailsBasePersistenceService {
  constructor(
    private genericHttpClientService: GenericHttpClientService,
    private dprDetailsSyncSanitizeService: DprDetailsSyncSanitizeService
  ) {
    super();
  }

  getDprDetails(dprDetailId: number | string): Observable<DprDetails> {
    return this.genericHttpClientService.get({
      context: HttpDprDetailsContext.GetDprDetails,
      args: {
        dprDetailId,
      },
    });
  }

  //#region Sync methods

  // clearing the id for the added records needs to be done here, because we must keep the ids in case of offline saves,
  // to be able to change the added records after the first save

  syncDprActivities(
    data: DprActivitiesGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprActivitiesGridSyncResult> {
    return this.genericHttpClientService.post<
      DprActivitiesGridSyncResult,
      DprActivitiesGridSync
    >({
      context: HttpDprDetailsContext.SyncDprActivities,
      body: this.dprDetailsSyncSanitizeService.clearActivityAddedRecordIds(
        data
      ),
      args: {
        isAutoSyncFromAdminChange,
      },
    });
  }

  syncDprStatusTracker(
    data: DprStatusTrackerGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprStatusTracker> {
    return this.genericHttpClientService.post<
      DprStatusTracker,
      DprStatusTrackerGridSync
    >({
      context: HttpDprDetailsContext.SyncDprStatusTracker,
      body: this.dprDetailsSyncSanitizeService.clearStatusTrackerAddedRecordIds(
        data
      ),
      args: {
        isAutoSyncFromAdminChange,
      },
    });
  }

  syncDprWeather(
    data: DprWeatherGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprWeather> {
    return this.genericHttpClientService.post<DprWeather, DprWeatherGridSync>({
      context: HttpDprDetailsContext.SyncDprWeather,
      body: this.dprDetailsSyncSanitizeService.clearWeatherAddedRecordIds(data),
      args: {
        isAutoSyncFromAdminChange,
      },
    });
  }

  syncDprPersonnel(
    data: DprPersonnelGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprPersonnel> {
    return this.genericHttpClientService.post<
      DprPersonnel,
      DprPersonnelGridSync
    >({
      context: HttpDprDetailsContext.SyncDprPersonnel,
      body: this.dprDetailsSyncSanitizeService.clearPersonnelAddedRecordIds(
        data
      ),
      args: {
        isAutoSyncFromAdminChange,
      },
    });
  }

  syncDprConsumable(
    data: DprConsumableGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprConsumable> {
    return this.genericHttpClientService.post<
      DprConsumable,
      DprConsumableGridSync
    >({
      context: HttpDprDetailsContext.SyncDprConsumable,
      body: this.dprDetailsSyncSanitizeService.clearConsumableAddedRecordIds(
        data
      ),
      args: {
        isAutoSyncFromAdminChange,
      },
    });
  }

  syncDprHse(
    data: DprHseGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprHse> {
    return this.genericHttpClientService.post<DprHse, DprHseGridSync>({
      context: HttpDprDetailsContext.SyncDprHse,
      body: this.dprDetailsSyncSanitizeService.clearHseAddedRecordIds(data),
      args: {
        isAutoSyncFromAdminChange,
      },
    });
  }

  syncDprSummary(
    data: DprSummaryGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprSummaryGridSyncResult> {
    return this.genericHttpClientService.post<
      DprSummaryGridSyncResult,
      DprSummaryGridSync
    >({
      context: HttpDprDetailsContext.SyncDprSummary,
      body: this.dprDetailsSyncSanitizeService.clearSummaryAddedRecordIds(data),
      args: {
        isAutoSyncFromAdminChange,
      },
    });
  }

  syncDprForecastActivities(
    data: DprForecastActivitiesGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprForecastActivitiesGridSyncResult> {
    return this.genericHttpClientService.post<
      DprForecastActivitiesGridSyncResult,
      DprForecastActivitiesGridSync
    >({
      context: HttpDprDetailsContext.SyncDprForecastActivities,
      body: this.dprDetailsSyncSanitizeService.clearForecastActivitiesAddedRecordIds(
        data
      ),
      args: {
        isAutoSyncFromAdminChange,
      },
    });
  }

  copyDprForecastActivitiesFromPreviousDpr(
    options: CopyPreviousDprForecastActivitiesOptions
  ): Observable<CopyPreviousDprForecastActivitiesResult> {
    return this.genericHttpClientService.post<
      CopyPreviousDprForecastActivitiesResult,
      CopyPreviousDprForecastActivitiesOptions
    >({
      context: HttpDprDetailsContext.CopyDprForecastActivitiesFromPreviousDpr,
      body: options,
    });
  }

  syncDprFreeTextField(
    data: DprFreeTextFieldSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprFreeTextFieldSyncResult> {
    return this.genericHttpClientService.post<
      DprFreeTextFieldSyncResult,
      DprFreeTextFieldSync
    >({
      context: HttpDprDetailsContext.SyncDprFreeTextField,
      body: this.dprDetailsSyncSanitizeService.clearFreeTextFieldAddedRecordIds(
        data
      ),
      args: {
        isAutoSyncFromAdminChange,
      },
    });
  }

  syncDprProgress(
    data: DprProgressGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprProgress> {
    return this.genericHttpClientService.post<DprProgress, DprProgressGridSync>(
      {
        context: HttpDprDetailsContext.SyncDprProgress,
        body: this.dprDetailsSyncSanitizeService.clearProgressAddedRecordIds(
          data
        ),
        args: {
          isAutoSyncFromAdminChange,
        },
      }
    );
  }

  syncDprProgressWithoutTarget(
    data: DprProgressWithoutTargetGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprProgressWithoutTarget> {
    return this.genericHttpClientService.post<
      DprProgressWithoutTarget,
      DprProgressWithoutTargetGridSync
    >({
      context: HttpDprDetailsContext.SyncDprProgressWithoutTarget,
      body: this.dprDetailsSyncSanitizeService.clearProgressWithoutTargetAddedRecordIds(
        data
      ),
      args: {
        isAutoSyncFromAdminChange,
      },
    });
  }

  syncDprSummaryExternalComment(
    data: DprSummaryGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprSummaryGridSyncResult> {
    return this.genericHttpClientService
      .post<DprSummaryGridSyncResult, DprSummaryGridSync>({
        context: HttpDprDetailsContext.SyncDprSummaryExternalComment,
        body: this.dprDetailsSyncSanitizeService.clearSummaryAddedRecordIds(
          data
        ),
        args: {
          isAutoSyncFromAdminChange,
        },
      })
      .pipe(
        mergeMap((response) => {
          return of({
            ...response,
            isExternalCommentResult: true,
          });
        })
      );
  }

  syncDprTableOfColumns(
    data: DprTableOfColumnGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprTableOfColumns> {
    return this.genericHttpClientService.post<
      DprTableOfColumns,
      DprTableOfColumnGridSync
    >({
      context: HttpDprDetailsContext.SyncDprTableOfColumns,
      body: this.dprDetailsSyncSanitizeService.clearTableOfColumnsAddedRecordIds(
        data
      ),
      args: {
        isAutoSyncFromAdminChange,
      },
    });
  }

  syncDprAppendix(
    data: DprAppendixGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprAppendixGridSyncResult> {
    return this.genericHttpClientService.post<
      DprAppendixGridSyncResult,
      DprAppendixGridSync
    >({
      context: HttpDprDetailsContext.SyncDprAppendix,
      body: this.dprDetailsSyncSanitizeService.clearAppendixAddedRecordIds(
        data
      ),
      args: {
        isAutoSyncFromAdminChange,
      },
    });
  }

  syncDprHidden(
    data: DprHiddenGridSync,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprHiddenGridSyncResult> {
    return this.genericHttpClientService.post<
      DprHiddenGridSyncResult,
      DprHiddenGridSync
    >({
      context: HttpDprDetailsContext.SyncDprHidden,
      body: this.dprDetailsSyncSanitizeService.clearHiddenAddedRecordIds(data),
      args: {
        isAutoSyncFromAdminChange,
      },
    });
  }

  getPreviousDprDetailsId(dprDate: Date): Observable<number | string> {
    const utcDate = convertDateToString(
      toUTCDate(new Date(dprDate), UtcDateTimeOptions.UtcDateWithoutTime)
    );

    return this.genericHttpClientService.get({
      context: HttpDprDetailsContext.GetPreviousDprDetailsId,
      args: {
        utcDate,
      },
    });
  }

  getNextDprDetailsId(dprDate: Date): Observable<number | string> {
    const utcDate = convertDateToString(
      toUTCDate(new Date(dprDate), UtcDateTimeOptions.UtcDateWithoutTime)
    );
    return this.genericHttpClientService.get({
      context: HttpDprDetailsContext.GetNextDprDetailsId,
      args: {
        utcDate,
      },
    });
  }

  isFirstDpr(dprDate: Date): Observable<boolean> {
    const utcDate = convertDateToString(
      toUTCDate(new Date(dprDate), UtcDateTimeOptions.UtcDateWithoutTime)
    );
    return this.genericHttpClientService.get({
      context: HttpDprDetailsContext.IsFirstDpr,
      args: {
        utcDate,
      },
    });
  }
}
